/* eslint-disable react-hooks/exhaustive-deps */
import cloneDeep from "lodash/cloneDeep";
import React, { useEffect, useState } from "react";

import jsonExport from "jsonexport/dist";

import { API, graphqlOperation } from "aws-amplify";
import { updateInvoice } from "../../../graphql/mutations";
import {
  syncableInvoiceCreateProps,
  syncableInvoiceUpdateProps,
} from "../../../graphql/invoice/invoiceCRUD";

import DateFnsUtils from "@date-io/date-fns";
import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import {
  Backdrop,
  CircularProgress,
  Grid,
  InputAdornment,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  Button,
  Confirm,
  downloadCSV,
  Edit,
  FileInput,
  FormDataConsumer,
  FormTab,
  Labeled,
  Loading,
  SelectInput,
  TabbedForm,
  TextInput,
  Toolbar,
  TopToolbar,
  useEditController,
  useNotify,
  useRedirect,
  useRefresh,
} from "react-admin";
import { useForm } from "react-final-form";

import {
  AttachFileIcon,
  CalendarIcon,
  ExportIcon,
} from "../../../components/common/iconography";
import { logError, logHistory } from "../../../utils/appUtils";
import { BackButton } from "../../../components/common/BackButton";
import { FileField } from "../../../components/common/FileField";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { HelpButton } from "../../../components/common/HelpButton";
import { HistoryTab } from "../../../components/common/HistoryTab";
import { CustomFields } from "../../../components/common/CustomFields";
import { ShowButton } from "../../../components/common/ShowButton";
import { ReSyncButton } from "../../../components/common/ReSyncButton";

import {
  ACCEPTED_FILES,
  ExternalStatus,
  InvoiceStatus,
  MAX_FILES,
  ResourceType,
} from "../../../utils/constants";
import { handleFileChange } from "../../../utils/fileUtils";
import { exportCustomField, getBackPath } from "../../../utils/utils";
import {
  fetchTriggerEnabled,
  getTriggerEnabled,
  hasExternalId,
  hasExternalStatus,
  addServiceNames,
  sanitizeZapierData,
  triggerEvent,
} from "../../../utils/zapierUtils";

import { fetchServices } from "../services/ServiceUtils";
import { InvoiceEditHelp } from "./InvoiceHelp";
import { DeepDeleteButton } from "./DeepDeleteButton";
import { MarkPaidButton } from "./MarkPaidButtons";
import { InvoicePaymentsPanel } from "./InvoicePaymentsPanel";
import { hasPayments } from "./invoicePaymentUtils";
import { ReopenButton } from "./ReopenButton";
import { ChargesTab } from "./ChargesTab";
import { InvoiceEditAside } from "./InvoiceEditAside";
import {
  getDueDate,
  getMemo,
  getPaymentTerms,
  getPaymentTermsName,
  getPoNumber,
  getServiceDate,
  setInvoiceSyncFailed,
  setInvoiceSyncPending,
} from "./InvoiceUtils";
import { SplitSaveButton } from "./SplitSaveButton";
import { SendInvoiceButton } from "./SendInvoiceButtons";
import { AccountTypeIcon } from "../../../components/common/AccountTypeIcon";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
  },
}));

const validateTermExists = (value, termList) => {
  if (!value) return undefined; // valid
  const isFound = termList.some((term) => term.id === value);
  if (!isFound) return "Required. Term no longer exists";
  return undefined; // valid
};

const InvoiceSetupTab = ({ record, account, isLocked, isVoid, ...props }) => {
  const form = useForm();
  const notify = useNotify();
  const [selectedDueDate, setSelectedDueDate] = useState(record.dueDate);

  const termList = window.appUser?.reloadYard?.paymentTerms?.items || [];

  const getAccountHelperText = (account) => {
    if (record?.account?.overLimit) {
      return "⚠️ Account over credit limit.";
    } else if (record?.account?.overDue) {
      return "⚠️ Account over due.";
    }

    if (account.billingAddress) {
      account.address = account.billingAddress;
      return account?.address
        ? `${account.address.street}, ${account.address.city}, ${account.address.state} ${account.address.zipCode}`
        : null;
    }

    return "";
  };

  // Subscribe to field state updates for 3rd party inputs
  form.registerField(
    "dueDate",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );
  const handleDueDateChange = (value) => {
    form.change("dueDate", value);
    setSelectedDueDate(value);
  };

  const handleTermsChange = (event) => {
    const value = event.target.value;
    const paymentTerm = getPaymentTerms(value);
    form.change("paymentTerms", paymentTerm?.name || "");

    if (value) {
      // On edit, if payment term is missing, do not change due date
      const dueDate = getDueDate(value);
      form.change("dueDate", dueDate);
      setSelectedDueDate(dueDate);
    }
  };

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => (
        <Grid container direction="row" spacing={1}>
          <Grid item xs={12} md={6}>
            <TextInput
              source="accountName"
              label="Account"
              variant="outlined"
              disabled
              fullWidth
              helperText={getAccountHelperText(account)}
              options={{
                error: record?.account?.overLimit || record?.account?.overDue,
              }}
              InputProps={{
                endAdornment: <AccountTypeIcon account={record?.account} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SelectInput
              source={"contact"}
              label="Point of Contact"
              variant="outlined"
              fullWidth
              disabled={!account || isLocked}
              choices={account?.billingContacts ? account.billingContacts : []}
              optionValue="name"
              optionText="name"
              allowEmpty
              resettable
              format={(v) => (!v ? "" : v.name)}
              parse={(v) =>
                !v ? "" : account.billingContacts.find((c) => c.name === v)
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput
              source="poNumber"
              label="PO Number"
              disabled={isLocked}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput
              source="invoiceNumber"
              label="Invoice Number"
              disabled={true}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            {isLocked ? (
              <TextInput
                source="paymentTerms"
                label="Payment Terms"
                disabled={true}
                variant="outlined"
                fullWidth
              />
            ) : (
              <SelectInput
                source="paymentTermId"
                label="Payment Terms"
                variant="outlined"
                fullWidth
                allowEmpty
                choices={termList}
                onChange={handleTermsChange}
                validate={(value) => validateTermExists(value, termList)}
                helperText={false}
              />
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <DatePicker
                label="Due Date"
                margin="dense"
                inputVariant="outlined"
                variant="dialog"
                fullWidth
                disablePast={!isLocked}
                disabled={isLocked || formData.paymentTermId}
                required
                error={!selectedDueDate}
                helperText={selectedDueDate ? false : "Required"}
                value={selectedDueDate ? selectedDueDate : null}
                format="MM/dd/yyyy" // display format
                onChange={handleDueDateChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <CalendarIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </MuiPickersUtilsProvider>
          </Grid>
          <Grid item xs={12}>
            <TextInput
              source="notes"
              label="Invoice Notes"
              parse={(v) => (v !== "" ? v : null)}
              disabled={isLocked}
              multiline
              minRows="5"
              variant="outlined"
              fullWidth
            />
          </Grid>
          <CustomFields
            {...props}
            accountFields={account?.invoiceCustomFields}
            width={6}
            disabled={isLocked}
          />
          <Grid item xs={12}>
            {isVoid ? (
              // FileInput ignores disabled, so show voided attachments read-only
              <Labeled label="ATTACHMENTS">
                <FileField
                  record={record}
                  source="attachments"
                  src="src"
                  title="title"
                  target="_blank"
                />
              </Labeled>
            ) : (
              <FileInput
                source="attachments"
                label="ATTACHMENTS"
                placeholder={
                  <p>
                    <AttachFileIcon fontSize="large" /> <br /> Drop file(s) to
                    upload, or click to select.
                  </p>
                }
                accept={ACCEPTED_FILES}
                multiple
                maxFiles={MAX_FILES}
                onChange={(value) =>
                  handleFileChange("attachments", value, form, notify)
                }
                variant="outlined"
                fullWidth
                options={{ maxFiles: MAX_FILES }}
              >
                <FileField source="src" title="title" target="_blank" />
              </FileInput>
            )}
          </Grid>
        </Grid>
      )}
    </FormDataConsumer>
  );
};

const exportCSV = (data, setOpen, notify) => {
  let invoicesForExport = [];
  const customHeaderMap = new Map();
  const invoice = data;
  debugger;

  const handleUndefined = (item) => (!item ? "" : item);

  try {
    if (data.lineItems?.items) {
      invoicesForExport = data.lineItems.items.map((element) => {
        const invoiceLineItem = {
          InvoiceNo: invoice.invoiceNumber,
          Customer: invoice.accountName,
          InvoiceDate: new Date(invoice.createdAt).toLocaleDateString(),
          DueDate: new Date(invoice.dueDate).toLocaleDateString(),
          Terms: getPaymentTermsName(invoice.paymentTermId),
          Memo: getMemo(invoice),
          Item: element.service?.name || "",
          ItemDescription: handleUndefined(element.serviceNote),
          ItemQuantity: element.quantity,
          ItemRate: element.unitCost,
          ItemAmount: element.amount,
          ItemTaxCode: element.taxRate.taxCode,
          ItemTaxAmount: element.itemTax,
          Currency: "USD",
          ServiceDate: getServiceDate(invoice, element),
        };

        // OPTIONAL VALUES
        invoiceLineItem.poNumber = getPoNumber(invoice, element);
        if (invoice.customFields?.length > 0) {
          invoice.customFields.forEach((field) => {
            exportCustomField(field, invoiceLineItem, customHeaderMap);
          });
        }

        return invoiceLineItem;
      });
    }

    jsonExport(
      invoicesForExport,
      {
        rename: [
          "InvoiceNo",
          "Customer",
          "InvoiceDate",
          "DueDate",
          "Terms",
          "Memo",
          "Item(Product/Service)",
          "ItemDescription",
          "ItemQuantity",
          "ItemRate",
          "ItemAmount",
          "ItemTaxCode",
          "ItemTaxAmount",
          "Currency",
          "ServiceDate",
          // ...customHeaderMap.keys()
        ], // order fields in the export
      },
      (err, csv) => {
        downloadCSV(csv, `invoice_${data.invoiceNumber}`); // download as 'invoice_InvoiceNo.csv` file
      },
    );

    if (!data.externalStatus && data.status === InvoiceStatus.NEW)
      setOpen(true);
  } catch (error) {
    logError("invoices:InvoiceEdit:exportCSV", error, notify);
  }
};

const handlePendingUpdate = async (
  data,
  setOpen,
  setSaving,
  notify,
  redirect,
  refresh,
) => {
  setSaving(true);
  debugger;

  try {
    const input = {
      id: data.id,
      active: data.active,
      reloadYardId: data.reloadYardId,
      dueDate: data.dueDate,
      accountId: data.accountId,
      accountName: data.accountName,
      status:
        data.status === InvoiceStatus.NEW ? InvoiceStatus.PENDING : data.status,
    };

    await API.graphql(graphqlOperation(updateInvoice, { input }));
    const historyNote = {
      parentId: data.id,
      parentType: ResourceType.INVOICE,
      text: `Invoice exported.`,
    };
    logHistory(historyNote);

    refresh();
  } catch (err) {
    logError("invoices:InvoiceEdit:handlePendingUpdate", err, notify, redirect);
  } finally {
    setSaving(false);
    setOpen(false);
  }
};

const EditActions = ({ data, isSyncEnabled, ...props }) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  const handleResync = async (record) => {
    try {
      await setInvoiceSyncPending(record);
      const resyncData = cloneDeep(data);
      addServiceNames(resyncData);
      if (hasExternalId(record)) {
        const sanitizedData = sanitizeZapierData(
          syncableInvoiceUpdateProps,
          resyncData,
        );
        // handled by multi-steps triggerEvent
        triggerEvent(sanitizedData, "invoiceUpdated", {
          autoSend: false,
        }).catch((error) => {
          logError("invoices:InvoiceEdit:handleResync", error, notify);
          setInvoiceSyncFailed(record);
        });
      } else {
        const sanitizedData = sanitizeZapierData(
          syncableInvoiceCreateProps,
          resyncData,
        );
        triggerEvent(sanitizedData, "invoiceCreated").catch((error) => {
          logError("invoices:InvoiceEdit:handleResync", error, notify);
          setInvoiceSyncFailed(record);
        });
      }
    } catch (error) {
      setInvoiceSyncFailed(record);
      logError("invoices:InvoiceEdit:handleResync", error, notify);
    }
  };

  return (
    <TopToolbar>
      <HelpButton>
        <InvoiceEditHelp {...props} />
      </HelpButton>
      <ReSyncButton
        record={data}
        isSyncEnabled={isSyncEnabled}
        syncFn={handleResync}
      />
      <SendInvoiceButton {...props} record={data} />
      {!data.externalStatus && <MarkPaidButton {...props} record={data} />}
      {!data.externalStatus && <ReopenButton {...props} record={data} />}
      <Button label="Export" onClick={() => exportCSV(data, setOpen, notify)}>
        <ExportIcon />
      </Button>
      {isSyncEnabled && !data.externalUrl ? (
        <Tooltip title="Pending external sync." arrow placement="left">
          <span>
            <ShowButton label="View" record={data} disabled />
          </span>
        </Tooltip>
      ) : (
        <ShowButton {...props} label="View" record={data} />
      )}
      <BackButton {...props} />
      <Confirm
        isOpen={open}
        loading={saving}
        title="Advance Confirmation"
        content="Advance this Invoice to PENDING status?"
        onConfirm={() =>
          handlePendingUpdate(
            data,
            setOpen,
            setSaving,
            notify,
            redirect,
            refresh,
          )
        }
        onClose={() => setOpen(false)}
      />
    </TopToolbar>
  );
};

const validateEdit = (values) => {
  const errors = {};
  if (!values.dueDate) {
    errors.dueDate = ["Due Date is required"];
  }

  return errors;
};

const EditToolbar = (props) => {
  const classes = useStyles();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { backPath, record, isSyncEnabled, isLocked } = props;

  return (
    <Toolbar style={{ justifyContent: "space-between" }}>
      <Backdrop className={classes.backdrop} open={isSaving || isDeleting}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <SplitSaveButton
        {...props}
        label="Save"
        record={record}
        backPath={backPath}
        isSaving={isSaving}
        setIsSaving={setIsSaving}
        isDeleting={isDeleting}
        isSyncEnabled={isSyncEnabled}
        isLocked={isLocked}
      />
      {record.status !== InvoiceStatus.PAID ? (
        <DeepDeleteButton
          {...props}
          isSaving={isSaving}
          isDeleting={isDeleting}
          setIsDeleting={setIsDeleting}
          isSyncEnabled={isSyncEnabled}
          isLocked={isLocked}
        />
      ) : null}
    </Toolbar>
  );
};

export const InvoiceEdit = (props) => {
  const notify = useNotify();
  const { record } = useEditController(props);
  const isSentOrPaid =
    record?.status === InvoiceStatus.PENDING ||
    record?.status === InvoiceStatus.PAID;
  const isVoid = record?.status === InvoiceStatus.VOID;
  const [isLocked, setIsLocked] = useState(isSentOrPaid || isVoid);
  const [services, setServices] = useState([]);
  const [isSyncEnabled, setIsSyncEnabled] = useState(
    getTriggerEnabled("invoiceUpdated"),
  );
  const backPath = getBackPath(props);

  useEffect(() => {
    const checkSyncEnabled = async () => {
      const triggerEnabled = await fetchTriggerEnabled("invoiceUpdated");
      const wasSynced = hasExternalStatus(record); // separate from legacy invoices created before zaper sync
      setIsSyncEnabled(triggerEnabled && wasSynced);
    };

    const fetchServiceData = async () => {
      try {
        const allServices = await fetchServices(record.accountId, true, true);
        setServices(allServices);
      } catch (error) {
        logError("invoices:InvoiceEdit:fetchServiceData", error, notify);
      }
    };

    if (record) {
      checkSyncEnabled();
      fetchServiceData();
      if (isVoid) {
        setIsLocked(true);
      } else if (record.externalStatus) {
        setIsLocked(record.externalStatus !== ExternalStatus.SYNCED);
      } else {
        setIsLocked(isSentOrPaid);
      }
    }
  }, [record, isSentOrPaid, isVoid]);

  if (!record) return <Loading />;

  // Payments are recorded in transloads.co only when no accounting sync owns
  // the invoice; keep the tab for any invoice that already has some.
  const showPayments = !record.externalStatus || hasPayments(record);
  return (
    <Edit
      {...props}
      title={<ForceTitle title="Edit Invoice" />}
      mutationMode="pessimistic"
      aside={
        <InvoiceEditAside {...props} backPath={backPath} record={record} />
      }
      actions={
        <EditActions
          {...props}
          backPath={backPath}
          isSyncEnabled={isSyncEnabled}
        />
      }
    >
      <TabbedForm
        submitOnEnter={false}
        validate={validateEdit}
        toolbar={
          <EditToolbar
            {...props}
            backPath={backPath}
            record={record}
            isLocked={isLocked}
            isSyncEnabled={isSyncEnabled}
          />
        }
      >
        <FormTab path="" label="Invoice Setup">
          <InvoiceSetupTab
            record={record}
            account={record.account}
            isLocked={isLocked}
            isVoid={isVoid}
          />
        </FormTab>
        <FormTab path="charges" label="Charges">
          <ChargesTab
            account={record.account}
            services={services}
            isSyncEnabled={isSyncEnabled}
            isLocked={isLocked}
          />
        </FormTab>
        {showPayments ? (
          <FormTab path="payments" label="Payments">
            <InvoicePaymentsPanel record={record} />
          </FormTab>
        ) : null}
        <FormTab path="historyLog" label="History Log">
          <HistoryTab />
        </FormTab>
      </TabbedForm>
    </Edit>
  );
};
